// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify/Notify";
import UserAccountForm from "@guestsupply/App/Components/UserAccountForm/UserAccountForm";
import createUserAccounts from "@guestsupply/App/Handlers/RequestWebsiteAccess/CreateUserAccounts";
import validateAccountNumberHandler from "@guestsupply/App/Handlers/RequestWebsiteAccess/ValidateAccountNumberHandler";
import { SectionTitleStyles } from "@guestsupply/App/Widgets/SectionTitle/SectionTitle";
import { Step } from "@guestsupply/App/Widgets/Stepper/helpers";
import StepperContextProvider, { StepperContext } from "@guestsupply/App/Widgets/Stepper/StepperContext";
import Button from "@guestsupply/MobiusOverrides/Button/Button";
import TextField, { TextFieldProps } from "@guestsupply/MobiusOverrides/TextField/TextField";
import AddressInfoDisplay from "@guestsupply/Overrides/Components/AddressInfoDisplay";
import { fontFamilyValues } from "@guestsupply/XDL/constants";
import { customXngageWidget } from "@guestsupply/XDL/constants";
import Plus from "@guestsupply/XDL/Icons/Plus";
import { AccountInfoModel, UserAccountModel } from "@guestsupply/XDL/types";
import { color, isURL, xValidateOnSubmit } from "@guestsupply/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonIconPresentationProps } from "@insite/mobius/Button";
import { Icon, LoadingOverlay } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    validateAccountNumberHandler,
    createUserAccounts,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext & HasHistory;

interface ProgressBarSectionStyles {
    wrapper?: InjectableCss;
    dotsWrapper: TypographyPresentationProps;
    labelsWrapper?: InjectableCss;
    dot?: TypographyPresentationProps;
    firstInnerDot?: TypographyPresentationProps;
    secondInnerDot?: TypographyPresentationProps;
    line?: TypographyPresentationProps;
}

export interface RequestWebsiteAccessStyles {
    wrapper?: InjectableCss;
    progressBarSection?: ProgressBarSectionStyles;
    accountNumberSectionWrapper?: InjectableCss;
    accountNumberTitle?: SmallHeadingAndTextStyles;
    sectionTitle?: TypographyProps;
    addressTitle?: SectionTitleStyles;
    accountNumberTextField?: TextFieldProps;
    accountNumberNotify?: TypographyProps;
    nextButton?: ButtonIconPresentationProps;
    addUserLinkWrapper?: InjectableCss;
    addAdditionalUserLink?: LinkPresentationProps;
    addUserButton?: ButtonIconPresentationProps;
    buttonsWrapper?: InjectableCss;
    addressWrapper?: InjectableCss;
    submittingAccountsNotify?: TypographyProps;
    notifyWrapper?: InjectableCss;
    toastTypography?: TypographyProps;
}

export const requestWebsiteAccessStyles: RequestWebsiteAccessStyles = {};

const styles = requestWebsiteAccessStyles;

const requestAccessSteps: Step[] = [
    {
        index: 1,
        name: `${translate("Account Number")}`,
        prev: -1,
        displayName: `${translate("Account Number")}`,
        next: 2,
    },
    {
        index: 2,
        name: `${translate("User Information")}`,
        prev: 1,
        displayName: `${translate("User Information")}`,
    },
];
const formId = "account-number-form";

const RequestWebsiteAccess: React.FC<Props> = ({
    validateAccountNumberHandler,
    createUserAccounts,
    history,
    toaster,
}) => {
    const [accountNumber, setAccountNumber] = useState("");
    const [isSubmittingAccountNumber, setIsSubmittingAccountNumber] = useState(false);
    const [accountNumberValidationErrorMessage, setAccountNumberValidationErrorMessage] = useState("");
    const [accountInfo, setAccountInfo] = useState<AccountInfoModel | undefined>(undefined);
    const [userAccounts, setUserAccounts] = useState<UserAccountModel[]>([{ phone: ["", "", ""] } as UserAccountModel]);
    const [isDuplicateEmailsError, setIsDuplicateEmailsError] = useState(false);
    const [isSubmittingAccounts, setIsSubmittingAccounts] = useState(false);
    const [submittingAccountsErrorMessage, setSubmittingAccountsErrorMessage] = useState("");

    const validateUserAccountNumber = () => {
        return !!accountNumber;
    };

    const goToPreviousStepHandler = (setCurrentStepHandler: any) => {
        setCurrentStepHandler(1);
        setAccountNumber("");
        setAccountInfo(undefined);
        setUserAccounts([{ phone: ["", "", ""] } as UserAccountModel]);
        setIsDuplicateEmailsError(false);
        setSubmittingAccountsErrorMessage("");
    };

    const nextButtonClickHandler = (e: any, setCurrentStepHandler: any) => {
        setAccountNumberValidationErrorMessage("");
        const errors = xValidateOnSubmit(formId);
        if (errors.length) {
            return;
        }
        setIsSubmittingAccountNumber(true);
        validateAccountNumberHandler({
            accountNumber,
            onSuccess: (result: AccountInfoModel) => {
                setCurrentStepHandler(2);
                setAccountNumberValidationErrorMessage("");
                setAccountInfo(result);
                setUserAccounts([{ phone: ["", "", ""] } as UserAccountModel]);
            },
            onError: (error: any) => {
                let validateAccountNumberError = error.errorMessage;
                if (isURL(accountNumber)) {
                    validateAccountNumberError = siteMessage("Custom_Not_Validate_Account");
                }
                setAccountNumberValidationErrorMessage(validateAccountNumberError);
            },
            onComplete: () => {
                setIsSubmittingAccountNumber(false);
            },
        });
    };

    const removeUser = (index: number) => {
        const updatedAccounts = [...userAccounts];
        updatedAccounts.splice(index, 1);
        setUserAccounts(updatedAccounts);
    };

    const userAccountChangeHandler = (userAccount: UserAccountModel, index: number) => {
        const updatedAccounts = [...userAccounts];
        updatedAccounts[index] = userAccount;
        setUserAccounts(updatedAccounts);
    };

    const createAdditionalUser = (event: React.MouseEvent<Element, MouseEvent>) => {
        event.preventDefault();
        userAccounts.length < 3 && setUserAccounts([...userAccounts, { phone: ["", "", ""] } as UserAccountModel]);
    };

    const StyledForm = getStyledWrapper("form");

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsDuplicateEmailsError(false);
        setSubmittingAccountsErrorMessage("");
        let isDuplicateEmail = false;
        const formsIds: string[] = [];
        userAccounts.forEach((account, index) => {
            formsIds.push(`userAccountForm-${index}`);
        });
        const errors: any[] = [];
        formsIds.forEach(formId => {
            const formError = xValidateOnSubmit(formId);
            formError.length > 0 && errors.push(formError);
        });

        if (errors.length > 0) {
            return;
        }
        const tempArr: string[] = [];
        userAccounts.forEach((acc, idx) => {
            userAccounts[idx]["phoneNumber"] =
                userAccounts[idx]["phone"][0] + userAccounts[idx]["phone"][1] + userAccounts[idx]["phone"][2];

            userAccounts[idx]["customerNumber"] = accountNumber;
            tempArr.push(userAccounts[idx]["email"]);

            if (userAccounts[idx + 1] && tempArr.indexOf(userAccounts[idx + 1]["email"]) !== -1) {
                isDuplicateEmail = true;
                setIsDuplicateEmailsError(true);
                return false;
            }
        });

        if (isDuplicateEmail) {
            return;
        }

        setIsSubmittingAccounts(true);
        createUserAccounts({
            accounts: userAccounts,
            companyName: accountInfo?.companyName,
            onSuccess: () => {
                toaster.addToast({
                    body: (
                        <>
                            <Typography as="p" {...styles.toastTypography}>
                                {siteMessage("Guest_Request_Access_Success_Message_Title_MSG")}
                            </Typography>
                            <Typography as="p">{siteMessage("Guest_Request_Access_Success_Message_MSG")}</Typography>
                        </>
                    ),
                    messageType: "success",
                    timeoutLength: 5000,
                });
                setTimeout(() => {
                    history.push("/");
                }, 500);
            },
            onError: (error: any) => {
                setSubmittingAccountsErrorMessage(error.errorMessage);
            },
            onComplete: () => {
                setIsSubmittingAccounts(false);
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StepperContextProvider initialStep={1} allowStepClick={true} steps={requestAccessSteps}>
                <StepperContext.Consumer>
                    {({ showStep, setCurrentStep }) => {
                        return (
                            <>
                                {createWidgetElement("Stepper/Stepper", {
                                    fields: {},
                                })}
                                {showStep(1) && (
                                    <LoadingOverlay loading={isSubmittingAccountNumber}>
                                        <StyledWrapper {...styles.accountNumberSectionWrapper}>
                                            <Typography as="p" {...styles.sectionTitle}>
                                                {translate("Customer Number Validation")}
                                            </Typography>
                                            <TextField
                                                id="accountNumber"
                                                {...styles.accountNumberTextField}
                                                label={translate("Account Number")}
                                                value={accountNumber}
                                                required
                                                onChange={e => setAccountNumber(e.currentTarget.value)}
                                                maxLength={25}
                                                {...{ formId }}
                                            />
                                            {accountNumberValidationErrorMessage && (
                                                <Notify {...styles.accountNumberNotify} type="danger">
                                                    <StyledWrapper>
                                                        {parse(accountNumberValidationErrorMessage)}
                                                    </StyledWrapper>
                                                </Notify>
                                            )}
                                            <Button
                                                {...styles.nextButton}
                                                disabled={isSubmittingAccountNumber}
                                                onClick={e => nextButtonClickHandler(e, setCurrentStep)}
                                                aria-label="Next"
                                            >
                                                {translate("Next")}
                                            </Button>
                                        </StyledWrapper>
                                    </LoadingOverlay>
                                )}
                                {showStep(2) && (
                                    <StyledWrapper {...styles.accountNumberSectionWrapper}>
                                        <SmallHeadingAndText
                                            extendedStyles={styles.accountNumberTitle}
                                            heading={translate("Sysco Guest Supply Account Number")}
                                            text={accountNumber}
                                        />
                                        {accountInfo && (
                                            <StyledWrapper {...styles.addressWrapper}>
                                                {createWidgetElement("SectionTitle/SectionTitle", {
                                                    fields: {
                                                        title: translate("Property Information"),
                                                        fontFamily: fontFamilyValues.GOTHAM,
                                                        border: "bottom",
                                                    },
                                                    extendedStyles: styles.addressTitle,
                                                } as any)}
                                                <AddressInfoDisplay
                                                    companyName={accountInfo.companyName}
                                                    address1={accountInfo.address}
                                                    city={accountInfo.city}
                                                    state={accountInfo.state}
                                                    postalCode={accountInfo.postalCode}
                                                    phone={accountInfo.phoneNumber}
                                                    country={accountInfo.country}
                                                />
                                            </StyledWrapper>
                                        )}
                                        <LoadingOverlay loading={isSubmittingAccounts}>
                                            <StyledForm onSubmit={handleFormSubmit}>
                                                {userAccounts &&
                                                    userAccounts.length > 0 &&
                                                    userAccounts.map((userAccount, index) => (
                                                        <UserAccountForm
                                                            removeAddtionalUser={() => removeUser(index)}
                                                            userAccount={userAccount}
                                                            key={index}
                                                            index={index}
                                                            formId={`userAccountForm-${index}`}
                                                            onChange={newData =>
                                                                userAccountChangeHandler(newData, index)
                                                            }
                                                        />
                                                    ))}
                                                {userAccounts.length < 3 && (
                                                    <>
                                                        <StyledWrapper {...styles.addUserLinkWrapper}>
                                                            <Typography>{translate("Additional Users")}</Typography>
                                                            <Link
                                                                onClick={createAdditionalUser}
                                                                {...styles.addAdditionalUserLink}
                                                                aria-label={translate("Add User")}
                                                            >
                                                                <Icon src={Plus} />
                                                            </Link>
                                                        </StyledWrapper>

                                                        <Typography as="p">
                                                            {siteMessage("Guest_Additional_Users_MSG")}
                                                        </Typography>
                                                        <Button
                                                            {...styles.addUserButton}
                                                            onClick={createAdditionalUser}
                                                            aria-label="Add User"
                                                        >
                                                            {translate("Add User")}
                                                        </Button>
                                                        <Notify type="info">
                                                            {siteMessage("Guest_Additional_Users_Warning_MSG")}
                                                        </Notify>
                                                    </>
                                                )}

                                                {isDuplicateEmailsError && (
                                                    <StyledWrapper {...styles.notifyWrapper}>
                                                        <Notify type="danger">
                                                            {siteMessage("Guest_DuplicateEmail_Error_Msg")}
                                                        </Notify>
                                                    </StyledWrapper>
                                                )}

                                                {submittingAccountsErrorMessage && (
                                                    <StyledWrapper {...styles.notifyWrapper}>
                                                        <Notify {...styles.submittingAccountsNotify} type="danger">
                                                            {submittingAccountsErrorMessage}
                                                        </Notify>
                                                    </StyledWrapper>
                                                )}

                                                <StyledWrapper {...styles.buttonsWrapper}>
                                                    <Button
                                                        variant="tertiary"
                                                        onClick={() => goToPreviousStepHandler(setCurrentStep)}
                                                        aria-label="Previous"
                                                    >
                                                        {translate("Previous")}
                                                    </Button>
                                                    <Button
                                                        disabled={isSubmittingAccounts}
                                                        type="submit"
                                                        aria-label="Submit"
                                                    >
                                                        {translate("Submit")}
                                                    </Button>
                                                </StyledWrapper>
                                            </StyledForm>
                                        </LoadingOverlay>
                                    </StyledWrapper>
                                )}
                            </>
                        );
                    }}
                </StepperContext.Consumer>
            </StepperContextProvider>
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(withToaster(withHistory(RequestWebsiteAccess))),
    definition: {
        group: customXngageWidget as any,
        allowedContexts: ["GuestRequestAccessPage" as any],
    },
};

export default widgetModule;
